import { useEffect, useState } from 'react';
import { AppShell } from '../../components/app/AppShell';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { InputField } from '../../components/ui/InputField';
import { Modal } from '../../components/ui/Modal';
import { Toggle } from '../../components/ui/Toggle';
import { api, ApiError } from '../../lib/api';
import type { Me, SessionSummary } from '../../lib/types';

const navItems = [
  { label: 'Карта', to: '/app' },
  { label: 'Профиль', to: '/app/profile' },
  { label: 'Безопасность', to: '/app/profile' },
  { label: 'Уведомления' },
  { label: 'Интерфейс', to: '/app/profile' },
  { label: 'Сессии', to: '/app/profile' },
];

function initialsOf(me: Me | null): string {
  if (!me) return '…';
  const a = me.first_name?.[0] ?? '';
  const b = me.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

export function ProfileSettingsPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [preferences, setPreferences] = useState<Record<string, unknown>>({});
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [totpModalOpen, setTotpModalOpen] = useState(false);
  const [sessionsModalOpen, setSessionsModalOpen] = useState(false);

  const load = async () => {
    try {
      const [meResponse, prefsResponse] = await Promise.all([api.getMe(), api.getPreferences()]);
      setMe(meResponse);
      setFullName([meResponse.first_name, meResponse.last_name].filter(Boolean).join(' '));
      setPhone(meResponse.phone ?? '');
      setPosition(meResponse.position ?? '');
      setPreferences(prefsResponse.preferences);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить профиль.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSaveProfile = async () => {
    setSaveError(null);
    setSaving(true);
    setSaved(false);
    try {
      const [first_name, ...rest] = fullName.trim().split(/\s+/);
      const updated = await api.updateMe({
        first_name,
        last_name: rest.join(' ') || undefined,
        phone,
        position,
      });
      setMe(updated);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить изменения.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarError(null);
    setAvatarUploading(true);
    try {
      const updated = await api.uploadAvatar(file);
      setMe(updated);
    } catch (err) {
      setAvatarError(err instanceof ApiError ? err.message : 'Не удалось загрузить фото.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const updatePreference = async (key: string, value: unknown) => {
    const next = { ...preferences, [key]: value };
    setPreferences(next);
    try {
      await api.updatePreferences({ [key]: value });
    } catch {
      // верстка не блокирует UI на ошибке автосохранения одной настройки
    }
  };

  const hasTotp = me?.mfa_factors.includes('totp') ?? false;

  return (
    <AppShell
      orgLabel={me?.workspace?.organization_name ?? '—'}
      navItems={navItems}
      backLabel="Вернуться на карту"
      backTo="/app"
      title="Профиль и настройки"
      breadcrumb="Личный кабинет / Профиль"
      userInitials={initialsOf(me)}
      userAvatarUrl={me?.avatar_url}
    >
      {loadError && <p className="mb-4 text-xs font-medium text-status-warning">{loadError}</p>}

      <div className="mx-auto grid max-w-[1200px] grid-cols-[1fr_380px] gap-6">
        <div className="flex flex-col gap-6">
          <Card title="Основные данные">
            <div className="flex items-center gap-4">
              <Avatar initials={initialsOf(me)} imageUrl={me?.avatar_url} size={76} tone="accent" className="text-xl" />
              <div>
                <label className="cursor-pointer text-xs font-semibold text-action-primary">
                  {avatarUploading ? 'Загружаем…' : 'Изменить фото'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    disabled={avatarUploading}
                    onChange={handleAvatarSelected}
                  />
                </label>
                <p className="mt-1 text-[11px] text-text-secondary">PNG, JPG, WEBP или GIF, до 5 МБ</p>
                {avatarError && <p className="mt-1 text-[11px] font-medium text-status-warning">{avatarError}</p>}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4">
              <InputField label="ФИО" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              <InputField label="Рабочая почта" type="email" value={me?.email ?? ''} disabled />
              <InputField label="Телефон" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <InputField label="Должность" value={position} onChange={(e) => setPosition(e.target.value)} />
            </div>

            {saveError && <p className="mt-3 text-xs font-medium text-status-warning">{saveError}</p>}
            {saved && !saveError && <p className="mt-3 text-xs font-medium text-status-success">Сохранено</p>}

            <Button type="button" className="mt-6 w-[220px]" onClick={handleSaveProfile} disabled={saving}>
              {saving ? 'Сохраняем…' : 'Сохранить изменения'}
            </Button>
          </Card>

          <Card title="Безопасность">
            <div className="flex flex-col divide-y divide-border-default">
              <SecurityRow
                title="Пароль"
                meta="Сменить рабочий пароль"
                action="Изменить"
                onAction={() => setPasswordModalOpen(true)}
              />
              <SecurityRow
                title="Двухфакторная защита"
                meta={hasTotp ? 'Приложение-аутентификатор подключено' : 'Не настроена'}
                action={hasTotp ? 'Отключить' : 'Настроить'}
                onAction={async () => {
                  if (hasTotp) {
                    await api.disableTotp();
                    load();
                  } else {
                    setTotpModalOpen(true);
                  }
                }}
              />
              <SecurityRow
                title="Активные сессии"
                meta="Устройства с активным входом"
                action="Открыть"
                onAction={() => setSessionsModalOpen(true)}
              />
            </div>
          </Card>
        </div>

        <div className="flex flex-col gap-6">
          <Card title="Организация">
            <p className="text-[13px] font-semibold text-text-primary">
              {me?.workspace?.organization_name ?? '—'}
            </p>
            <p className="mt-1 text-[11px] text-text-secondary">
              Роль: {me?.workspace?.role_codes.join(', ') || '—'}
            </p>
            {me?.workspace && (
              <span
                className={`mt-3 inline-flex items-center rounded-full bg-bg-surface-secondary px-3 py-1.5 text-[10px] font-semibold ${
                  me.workspace.member_status === 'active' ? 'text-status-success' : 'text-text-secondary'
                }`}
              >
                ● {me.workspace.member_status === 'active' ? 'Активный' : me.workspace.member_status}
              </span>
            )}
          </Card>

          <Card title="Интерфейс и карта">
            <div className="flex flex-col gap-4">
              <Toggle
                label="Тёмная тема"
                checked={Boolean(preferences.dark_theme)}
                onChange={(e) => updatePreference('dark_theme', e.target.checked)}
              />
              <Toggle
                label="Компактная навигация"
                checked={Boolean(preferences.compact_nav ?? true)}
                onChange={(e) => updatePreference('compact_nav', e.target.checked)}
              />
              <Toggle
                label="Показывать подсказки ГИС"
                checked={Boolean(preferences.show_hints ?? true)}
                onChange={(e) => updatePreference('show_hints', e.target.checked)}
              />
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <SettingRow label="Язык" value={String(preferences.language ?? 'Русский')} />
              <SettingRow label="Единицы измерения" value={String(preferences.units ?? 'Метрические')} />
              <SettingRow label="Подложка по умолчанию" value={String(preferences.basemap ?? 'Светлая')} />
            </div>
          </Card>
        </div>
      </div>

      <ChangePasswordModal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
      <TotpSetupModal
        open={totpModalOpen}
        onClose={() => setTotpModalOpen(false)}
        onConfirmed={() => {
          setTotpModalOpen(false);
          load();
        }}
      />
      <SessionsModal open={sessionsModalOpen} onClose={() => setSessionsModalOpen(false)} />
    </AppShell>
  );
}

function SecurityRow({
  title,
  meta,
  action,
  onAction,
}: {
  title: string;
  meta: string;
  action: string;
  onAction: () => void;
}) {
  return (
    <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
      <div>
        <p className="text-xs font-semibold text-text-primary">{title}</p>
        <p className="mt-1 text-[10px] text-text-secondary">{meta}</p>
      </div>
      <button type="button" onClick={onAction} className="text-[10px] font-semibold text-action-primary">
        {action}
      </button>
    </div>
  );
}

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex h-[38px] items-center justify-between rounded-lg bg-bg-surface-secondary px-3 text-[10px]">
      <span className="font-medium text-text-secondary">{label}</span>
      <span className="font-semibold text-text-primary">{value} ▾</span>
    </div>
  );
}

function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentPassword('');
      setNewPassword('');
      setError(null);
      setDone(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.changePassword({ current_password: currentPassword, new_password: newPassword });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сменить пароль.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      {done ? (
        <div className="text-center">
          <p className="text-sm font-semibold text-status-success">Пароль изменён</p>
          <p className="mt-2 text-xs text-text-secondary">
            Остальные устройства будут разлогинены при следующем обновлении токена.
          </p>
          <Button type="button" className="mt-4 w-full" onClick={onClose}>
            Готово
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-text-primary">Смена пароля</h2>
          <InputField
            label="Текущий пароль"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
          <InputField
            label="Новый пароль"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={10}
            required
          />
          {error && <p className="text-xs font-medium text-status-warning">{error}</p>}
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Сохраняем…' : 'Сменить пароль'}
          </Button>
        </form>
      )}
    </Modal>
  );
}

function TotpSetupModal({
  open,
  onClose,
  onConfirmed,
}: {
  open: boolean;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSecret(null);
    setCode('');
    setError(null);
    api
      .setupTotp()
      .then((res) => setSecret(res.secret))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Не удалось начать настройку.'));
  }, [open]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.confirmTotp(code);
      onConfirmed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Неверный код.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={handleConfirm} className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-text-primary">Приложение-аутентификатор</h2>
        <p className="text-xs text-text-secondary">
          Добавьте этот ключ вручную в Google Authenticator, Яндекс Ключ или аналог, затем введите код
          из приложения.
        </p>
        <div className="rounded-lg bg-bg-surface-secondary p-3 text-center font-mono text-sm tracking-widest text-text-primary">
          {secret ?? '…'}
        </div>
        <InputField
          label="Код из приложения"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={6}
          required
        />
        {error && <p className="text-xs font-medium text-status-warning">{error}</p>}
        <Button type="submit" className="w-full" disabled={submitting || !secret}>
          {submitting ? 'Проверяем…' : 'Подтвердить'}
        </Button>
      </form>
    </Modal>
  );
}

function SessionsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setSessions(await api.listSessions());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить сессии.');
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      <h2 className="text-lg font-semibold text-text-primary">Активные сессии</h2>
      {error && <p className="mt-2 text-xs font-medium text-status-warning">{error}</p>}
      <div className="mt-4 flex flex-col gap-2">
        {sessions.length === 0 && !error && (
          <p className="text-xs text-text-secondary">Нет активных сессий.</p>
        )}
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-lg bg-bg-surface-secondary p-3">
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-text-primary">{s.user_agent ?? 'Неизвестное устройство'}</p>
              <p className="mt-1 text-[10px] text-text-secondary">
                {s.ip_address ?? '—'} · до {new Date(s.expires_at).toLocaleDateString('ru-RU')}
              </p>
            </div>
            <button
              type="button"
              className="text-[10px] font-semibold text-status-danger"
              onClick={async () => {
                await api.revokeSession(s.id);
                load();
              }}
            >
              Завершить
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
